import { Helmet } from 'react-helmet-async';
import { avatarUrl, normalizeMediaUrl } from '../utils/constants';

type SeoProps = {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  type?: 'website' | 'article';
  keywords?: string[];
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

const fallbackTitle = 'Rin';
const fallbackDescription = 'Personal writing, tools, games, and links powered by Rin.';

function envValue(name: string) {
  const metaEnv = import.meta.env as Record<string, string | undefined>;
  return metaEnv[name] || process.env[name];
}

export function plainText(value = '') {
  return value
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*]\([^)]+\)/g, ' ')
    .replace(/\[([^\]]+)]\([^)]+\)/g, '$1')
    .replace(/[#>*_`~\-[\](){}|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function truncateMeta(value = '', limit = 160) {
  const text = plainText(value);
  if (text.length <= limit) {
    return text;
  }
  return `${text.slice(0, limit - 1).trim()}…`;
}

export function absoluteUrl(value?: string) {
  if (!value) {
    return undefined;
  }
  if (/^https?:\/\//i.test(value)) {
    return value;
  }
  const origin = envValue('SITE_URL') || envValue('VITE_SITE_URL') || window.location.origin;
  return new URL(value.startsWith('/') ? value : `/${value}`, origin).toString();
}

export function siteTitle() {
  return envValue('NAME') || fallbackTitle;
}

export function siteDescription() {
  return envValue('DESCRIPTION') || fallbackDescription;
}

export function Seo({
  title,
  description,
  path,
  image,
  type = 'website',
  keywords,
  noindex = false,
  jsonLd,
}: SeoProps) {
  const baseTitle = siteTitle();
  const metaTitle = title ? `${title} - ${baseTitle}` : baseTitle;
  const metaDescription = truncateMeta(description || siteDescription());
  const canonical = absoluteUrl(path || window.location.pathname);
  const imageUrl = absoluteUrl(normalizeMediaUrl(image) || avatarUrl || '/favicon.png');
  const jsonLdItems = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={canonical} />
      <meta property="og:site_name" content={baseTitle} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      {jsonLdItems.map((item, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}
